import {CustomAttribute, ICustomAttributesValue} from 'src/app/core/service/core-models';
import {ICustomerId, IRentCalcDetails, Project} from 'src/app/custom/housing-core/services/housing-models';
import {ProgramFeeTable} from './program.model';

// Top level Project
export interface ProjectModel {
  id?: string;
  customerId?: string;
  siteId?: string;
  name: string;
  type: string;
  projectNumber?: string;
  projectCode: string;
  program: string;
  programName: string;
  programType: string;
  chartOfAccounts?: string;
  rentCalc?: IRentCalcDetails;
  projectOverrides?: ProjectOverride;
  projectSegmentCode?: string;
  ampProject?: string;
  dateOfFullAvailability?: Date;
  createdOn?: Date;
  createdBy?: string;
  tracsMailboxNumber?: string;
  programId?: string;
  customAttributes?: CustomAttribute[] | ICustomAttributesValue[];
  taxCreditSetAsides?: TaxCreditSetAsides;
}

export interface ProjectOverride {
  id?: string;
  programId?: string;
  projectId?: string;
  calcKey: string;
  assistanceCalcKey?: any;
  elderlyDeductionAge: number;
  allowableDependentDeduction: number;
  allowableElderlyDisabilityDeduction?: number;
  totalTenantPaymentPercentage?: any;
  nearElderAge: number;
  observeNahasda30Percent: boolean;
  minTTP: number;
  incomeLimitAreaId: string;
  taxCreditIncomeLimitAreaId?: any;
  rentLimitAreaId?: any;
  paymentStandardId?: string;
  imputedAssetRate?: number;
  monthOfFiscalYearEnd?: number;
  fundingSource?: string;
  requireHotmaUpdates: boolean;
  isUtilityAllowanceCalculations: boolean;
  projectFeeTable?: ProjectFeeTable[];
  programFeeTable?: ProgramFeeTable[];
}
export interface ProjectFeeTable {
  id?: string;
  projectOverridesId?: string;
  effectiveDate: Date;
  feeTableType: string;
  feeTableValues: FeeTableValue[];
  status?: number | undefined;
}

export interface FeeTableValue {
  id: string;
  feeTableId: string;
  householdSize: number;
  amount: number;
}

export interface TaxCreditSetAsides {
  projectId: string;
  siteId: string;
  unitsAt30?: number;
  unitsAt40?: number;
  unitsAt50?: number;
  unitsAt60?: number;
  unitsAtOther?: number;
}

export interface CreateProjectCommand {
  name: string;
  projectCode: string;
  projectNumber?: string;
  ampProject?: string;
  programId: string;
  dateOfFullAvailability?: Date;
  projectSegmentCode?: string;
  tracsMailboxNumber?: string;
  createdBy: string;
  customerId: string;
  siteId: string;
}

export interface UpdateProjectCommand {
  id?: string;
  name: string;
  projectCode: string;
  projectNumber?: string;
  ampProject?: string;
  dateOfFullAvailability?: Date;
  projectSegmentCode?: string;
  tracsMailboxNumber?: string;
  modifiedBy: string;
  customerId: string;
  siteId: string;
}

export interface CreateProjectOverrideCommand {
  projectId: string;
  calcKey?: string;
  assistanceCalcKey?: string;
  elderlyDeductionAge?: number;
  totalTenantPaymentPercentage?: number;
  allowableElderlyDisabilityDeduction?: number;
  nearElderAge?: number;
  observeNahasda30Percent?: boolean;
  minTTP?: number;
  createdBy: string;
  customerId: string;
  siteId: string;
  allowableDependentDeduction?: number;
  incomeLimitAreaId?: string;
  taxCreditIncomeLimitAreaId?: any;
  rentLimitAreaId?: any;
  paymentStandardId?: string;
  imputedAssetRate?: number;
  monthOfFiscalYearEnd?: number;
  fundingSource?: string;
  requireHotmaUpdates?: boolean;
  isUtilityAllowanceCalculations?: boolean;
  incomeBased: boolean;
}

export interface UpdateProjectOverrideCommand {
  id: string;
  calcKey?: string;
  assistanceCalcKey?: string;
  elderlyDeductionAge?: number;
  totalTenantPaymentPercentage?: number;
  allowableElderlyDisabilityDeduction?: number;
  nearElderAge?: number;
  observeNahasda30Percent?: boolean;
  minTTP?: number;
  modifiedBy: string;
  customerId: string;
  siteId: string;
  allowableDependentDeduction?: number;
  incomeLimitAreaId?: string;
  taxCreditIncomeLimitAreaId?: any;
  rentLimitAreaId?: any;
  paymentStandardId?: string;
  imputedAssetRate?: number;
  monthOfFiscalYearEnd?: number;
  fundingSource?: string;
  requireHotmaUpdates?: boolean;
  isUtilityAllowanceCalculations?: boolean;
  incomeBased: boolean;
}
export interface CreateProjectFeeTableCommand {
  projectOverridesId: string;
  effectiveDate: Date;
  feeTableType: string;
  feeTableValues: number[];
  project: string; // projectId
  createdBy: string;
  siteId: string;
  customerId: string;
}

export interface DeleteProjectOverrideCommand {
  id: string;
  customerId: string;
  siteId: string;
}
export interface UpdateProjectFeeTableCommand {
  id: string;
  effectiveDate: Date;
  feeTableValues: number[];
  project: string; // programId
  modifiedBy: string;
  siteId: string;
  customerId: string;
}

export interface DeleteProjectFeeTableCommand {
  id: string;
  deletedBy: string;
  project: string;
  siteId: string;
  customerId: string;
}

export interface UpdateTaxCreditSetAsidesCommand {
  customerId: string;
  projectId: string;
  siteId: string;
  unitsAt30?: number;
  unitsAt40?: number;
  unitsAt50?: number;
  unitsAt60?: number;
  unitsAtOther?: number;
}

export interface CreateTaxCreditSetAsidesCommand {
  customerId: string;
  projectId: string;
  siteId: string;
  unitsAt30?: number;
  unitsAt40?: number;
  unitsAt50?: number;
  unitsAt60?: number;
  unitsAtOther?: number;
}

export enum ProjectFeeStatus {
  Create = 0,
  CreateAndModified,
  Modified,
  Delete,
}
export interface IDataResponse {
  status: string;
  failureReason?: string;
}

export interface IUpdateProjectDataResponse {
  updateProject: IProjectDataResponse;
}
export interface ICreateProjectDataResponse {
  createProject: IProjectDataResponse;
}
export interface IProjectDataResponse extends IDataResponse {
  affectedEntity: Project;
}

export interface IProjectOverrideDataResponse extends IDataResponse {
  affectedEntity: ProjectOverride;
}

export interface IUpdateProjectOverrideDataResponse {
  updateProjectOverrides: IProjectOverrideDataResponse;
}
export interface IDeleteProjectOverrideDataResponse {
  deleteProjectOverrides: IProjectOverrideDataResponse;
}
export interface ICreateProjectOverrideDataResponse {
  createProjectOverrides: IProjectOverrideDataResponse;
}

export interface ICreateProjectAttributeDataResponse {
  createProjectAttributeValue: IDataResponse;
}

export interface IUpdateProjectAttributeDataResponse {
  updateProjectAttributeValue: IDataResponse;
}

export interface IProjectAttributeCommand extends ICustomerId {
  textValue: string;
  dateTimeValue: Date;
  booleanValue: boolean;
}

export interface UpdateProjectCustomAttributes {
  id: string;
  textValue?: string;
  booleanValue?: boolean;
  dateTimeValue?: Date;
  siteId?: string;
  customerId?: string;
}
export interface ITaxCreditSetAsidesDataResponse extends IDataResponse {
  affectedentity: TaxCreditSetAsides;
}

export interface IUpdateTaxCreditSetAsidesDataResponse {
  updateTaxCreditSetAsides: ITaxCreditSetAsidesDataResponse;
}

export interface ICreateTaxCreditSetAsidesDataResponse {
  createTaxCreditSetAsides: ITaxCreditSetAsidesDataResponse;
}
